/**
 * SCARCE — Mystery Reveal  (v2)
 * Vanilla JS, zero dependencies. Everything is scoped inside .scarce-mystery
 * containers and no-ops when the markup isn't on the page, so this file is
 * safe to load anywhere in the theme.
 *
 * Integration note: the purchase-type selector deliberately emits radios with
 * id="subscription-option" and data-selling-plan-id, because the theme's own
 * assets/product-form.js reads exactly those when building the add-to-cart
 * payload. That means subscriptions flow through the existing Recharge/cart
 * pipeline instead of a parallel one.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function money(cents, format) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents, format); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* =====================================================================
   * Quantity pills + live price on the buy button
   * =================================================================== */
  function initQty(root) {
    var wrap = root.querySelector('[data-sm-qty]');
    if (!wrap) return;

    var formId = wrap.getAttribute('data-form-id');
    var form = (formId && document.getElementById(formId)) ||
               root.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    var fmt = wrap.getAttribute('data-money-format') || '';
    var submitLabel = form.querySelector('.product-form__submit span');

    function currentUnitPrice() {
      // The selected plan can change the unit price (subscribe & save).
      var selected = root.querySelector('.sm-plan.is-selected');
      if (selected && selected.getAttribute('data-price')) {
        return parseInt(selected.getAttribute('data-price'), 10);
      }
      return parseInt(wrap.getAttribute('data-unit-price') || '0', 10);
    }

    function qtyInput() {
      var input = form.querySelector('input[name="quantity"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        form.appendChild(input);
      }
      return input;
    }

    function apply(qty) {
      qtyInput().value = qty;
      wrap.querySelectorAll('.sm-qty__btn').forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-qty') === String(qty));
      });
      if (submitLabel) {
        var unit = currentUnitPrice();
        submitLabel.setAttribute('data-sm-price', unit ? ' — ' + money(unit * qty, fmt) : '');
      }
    }

    wrap.addEventListener('click', function (e) {
      var btn = e.target.closest('.sm-qty__btn');
      if (btn) apply(btn.getAttribute('data-qty'));
    });

    // Recalculate whenever the purchase type changes.
    root.addEventListener('sm:planchange', function () {
      var active = wrap.querySelector('.sm-qty__btn.is-active') || wrap.querySelector('.sm-qty__btn');
      if (active) apply(active.getAttribute('data-qty'));
    });

    var start = wrap.querySelector('.sm-qty__btn.is-active') || wrap.querySelector('.sm-qty__btn');
    if (start) apply(start.getAttribute('data-qty'));
  }

  /* =====================================================================
   * Purchase type: one-time vs subscribe & save
   * =================================================================== */
  function initPlans(root) {
    var wrap = root.querySelector('[data-sm-plans]');
    if (!wrap) return;

    function select(label) {
      wrap.querySelectorAll('.sm-plan').forEach(function (l) {
        l.classList.toggle('is-selected', l === label);
        var input = l.querySelector('input');
        if (input) input.checked = (l === label);
      });
      root.dispatchEvent(new CustomEvent('sm:planchange', { bubbles: false }));
    }

    wrap.addEventListener('click', function (e) {
      var label = e.target.closest('.sm-plan');
      if (label) select(label);
    });

    wrap.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var label = e.target.closest('.sm-plan');
      if (label) { e.preventDefault(); select(label); }
    });

    var pre = wrap.querySelector('.sm-plan.is-selected') || wrap.querySelector('.sm-plan');
    if (pre) select(pre);
  }

  /* =====================================================================
   * Info tabs
   * =================================================================== */
  function initTabs(root) {
    var tabs = root.querySelector('[data-sm-tabs]');
    if (!tabs) return;
    var panels = root.querySelector('[data-sm-tabpanels]');

    tabs.addEventListener('click', function (e) {
      var btn = e.target.closest('.sm-tabs__btn');
      if (!btn) return;
      var key = btn.getAttribute('data-tab');
      tabs.querySelectorAll('.sm-tabs__btn').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
      });
      if (panels) {
        panels.querySelectorAll('.sm-tabpanel').forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-tab') === key);
        });
      }
    });
  }

  /* =====================================================================
   * "See the odds" jump link
   * =================================================================== */
  function initOddsLink(root) {
    var link = root.querySelector('[data-sm-odds-link]');
    if (!link) return;
    link.addEventListener('click', function () {
      var target = document.querySelector('.sm-odds');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* =====================================================================
   * Odds bars — animate to their percentage when scrolled into view
   * =================================================================== */
  function initOddsBars(root) {
    var bars = root.querySelectorAll('.sm-odds__bar span');
    if (!bars.length) return;

    function fill(bar) {
      var pct = parseFloat(bar.getAttribute('data-pct') || '0');
      if (isNaN(pct)) pct = 0;
      // Clamp, and give sub-1% tiers a visible sliver so they don't read as zero.
      bar.style.width = Math.max(Math.min(pct, 100), pct > 0 ? 1.5 : 0) + '%';
    }

    if (!('IntersectionObserver' in window)) {
      bars.forEach(fill);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { fill(entry.target); io.unobserve(entry.target); }
      });
    }, { threshold: 0.4 });
    bars.forEach(function (b) { io.observe(b); });
  }

  /* =====================================================================
   * Heavy Hitters quick-view modal
   * =================================================================== */
  function initHitters(root) {
    var grid = root.querySelector('[data-sm-hitters]');
    var modal = root.querySelector('[data-sm-modal]');
    if (!grid || !modal) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.sm-hit'));
    if (!cards.length) return;

    var current = 0;
    var lastFocus = null;
    var mediaEl = modal.querySelector('[data-sm-modal-media]');
    var titleEl = modal.querySelector('[data-sm-modal-title]');
    var oddsEl = modal.querySelector('[data-sm-modal-odds]');
    var valueEl = modal.querySelector('[data-sm-modal-value]');
    var descEl = modal.querySelector('[data-sm-modal-desc]');

    function render(i) {
      var card = cards[i];
      if (!card) return;
      current = i;

      var img = card.getAttribute('data-image');
      mediaEl.innerHTML = img
        ? '<img src="' + img + '" alt="' + (card.getAttribute('data-name') || '') + '">'
        : '';

      titleEl.textContent = card.getAttribute('data-name') || '';

      var odds = card.getAttribute('data-odds');
      oddsEl.textContent = odds || '';
      oddsEl.hidden = !odds;

      var value = card.getAttribute('data-value');
      valueEl.textContent = value ? 'FMV ' + value : '';
      valueEl.hidden = !value;

      var desc = card.getAttribute('data-desc');
      descEl.textContent = desc || '';
      descEl.hidden = !desc;
    }

    function open(i) {
      lastFocus = document.activeElement;
      render(i);
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var close = modal.querySelector('[data-sm-modal-close]');
      if (close) close.focus();
    }

    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    grid.addEventListener('click', function (e) {
      var card = e.target.closest('.sm-hit');
      if (card) open(cards.indexOf(card));
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-sm-modal-close]') || e.target.classList.contains('sm-modal__backdrop')) close();
      else if (e.target.closest('[data-sm-modal-prev]')) render((current - 1 + cards.length) % cards.length);
      else if (e.target.closest('[data-sm-modal-next]')) render((current + 1) % cards.length);
    });

    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') render((current - 1 + cards.length) % cards.length);
      else if (e.key === 'ArrowRight') render((current + 1) % cards.length);
    });
  }

  /* =====================================================================
   * Vault reel — a weighted preview spin.
   *
   * This is explicitly a PREVIEW: the landed item is labelled as an
   * illustration of the pool, never as the item the shopper will receive.
   * Weights come from the editor so the reel visually reflects the same
   * rarity distribution shown in the odds table.
   * =================================================================== */
  function initReel(root) {
    var reel = root.querySelector('[data-sm-reel]');
    if (!reel) return;

    var stage = reel.querySelector('.sm-reel__stage');
    var track = reel.querySelector('.sm-reel__track');
    var btn = reel.querySelector('[data-sm-reel-spin]');
    var out = reel.querySelector('[data-sm-reel-result]');
    if (!stage || !track || !btn) return;

    var base = Array.prototype.slice.call(track.children);
    if (!base.length) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Repeat the pool so the strip is long enough to travel through.
    var REPEATS = 9;
    for (var r = 0; r < REPEATS; r++) {
      base.forEach(function (node) { track.appendChild(node.cloneNode(true)); });
    }

    function pickWeighted() {
      var total = base.reduce(function (sum, n) {
        return sum + (parseFloat(n.getAttribute('data-weight')) || 1);
      }, 0);
      var roll = Math.random() * total;
      for (var i = 0; i < base.length; i++) {
        roll -= (parseFloat(base[i].getAttribute('data-weight')) || 1);
        if (roll <= 0) return i;
      }
      return base.length - 1;
    }

    var spinning = false;

    btn.addEventListener('click', function () {
      if (spinning) return;
      spinning = true;
      btn.disabled = true;
      if (out) out.textContent = '';

      var index = pickWeighted();
      var item = track.children[0];
      var itemWidth = item.getBoundingClientRect().width + 14; // width + gap
      // Land inside a late repeat so the strip has distance to decelerate.
      var landing = base.length * (REPEATS - 2) + index;
      var offset = landing * itemWidth - (stage.offsetWidth / 2) + (itemWidth / 2);

      track.style.transition = 'none';
      track.style.transform = 'translate3d(0,0,0)';
      // Force reflow so the reset takes effect before the animated transform.
      void track.offsetWidth;

      var duration = reduced ? 0.001 : 4.2;
      track.style.transition = 'transform ' + duration + 's cubic-bezier(0.12, 0.75, 0.12, 1)';
      track.style.transform = 'translate3d(-' + offset + 'px,0,0)';

      window.setTimeout(function () {
        spinning = false;
        btn.disabled = false;
        if (out) {
          var name = base[index].getAttribute('data-name') || '';
          out.textContent = name ? 'Landed on: ' + name : '';
        }
      }, duration * 1000 + 120);
    });
  }

  /* =====================================================================
   * Arrow-controlled horizontal carousels
   * =================================================================== */
  function initCarousels(root) {
    root.querySelectorAll('[data-sm-carousel]').forEach(function (car) {
      var track = car.querySelector('.sm-drops__track');
      if (!track) return;
      var prev = car.querySelector('[data-sm-prev]');
      var next = car.querySelector('[data-sm-next]');
      function step() {
        var card = track.firstElementChild;
        return card ? card.getBoundingClientRect().width + 16 : 200;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    });
  }

  /* =====================================================================
   * Autoplaying testimonial videos
   * Muted autoplay is the only kind browsers allow unattended. We also
   * pause offscreen videos so a row of them doesn't chew battery, and let
   * a tap unmute.
   * =================================================================== */
  function initVideos(root) {
    var vids = root.querySelectorAll('[data-sc-video]');
    if (!vids.length) return;

    vids.forEach(function (v) {
      v.muted = true;               // belt and braces for older iOS
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked - poster shows */ });

      var wrap = v.closest('[data-sc-video-wrap]');
      var btn = wrap && wrap.parentNode ? wrap.parentNode.querySelector('[data-sc-sound]') : null;

      function syncBtn() {
        if (!btn) return;
        var on = btn.querySelector('.sc-testi__sound-on');
        var off = btn.querySelector('.sc-testi__sound-off');
        if (on) on.hidden = v.muted;
        if (off) off.hidden = !v.muted;
        btn.setAttribute('aria-label', v.muted ? 'Unmute video' : 'Mute video');
      }

      function toggleSound(e) {
        e.preventDefault();
        v.muted = !v.muted;
        if (!v.muted) { v.volume = 1; v.play().catch(function () {}); }
        syncBtn();
      }

      v.addEventListener('click', toggleSound);
      if (btn) btn.addEventListener('click', toggleSound);
      syncBtn();
    });

    // Pause what isn't on screen.
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        if (en.isIntersecting) { v.play().catch(function () {}); }
        else if (!v.paused) { v.pause(); }
      });
    }, { threshold: 0.25 });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* =====================================================================
   * FAQ accordion
   * =================================================================== */
  function initFaq(root) {
    var list = root.querySelector('[data-sc-faq]');
    if (!list) return;
    list.addEventListener('click', function (e) {
      var btn = e.target.closest('.sc-faq__q');
      if (!btn) return;
      var item = btn.closest('.sc-faq__item');
      var open = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* =================================================================== */
  function init(scope) {
    (scope || document).querySelectorAll('.scarce-mystery').forEach(function (root) {
      if (root.dataset.smInit === '1') return;
      root.dataset.smInit = '1';
      initQty(root);
      initPlans(root);
      initTabs(root);
      initOddsLink(root);
      initOddsBars(root);
      initHitters(root);
      initReel(root);
      initCarousels(root);
      initFaq(root);
      initVideos(root);
    });
  }

  ready(function () { init(document); });

  // Theme editor: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
